/* ═══════════════════════════════════════════════════════════════════════════
   Sales by Channel / Order Source — pure data layer  (ZAR-2371)

   Both views come from GET /sales/live/v2/by-channel:
     • Channel view — no `type` param → one row per channel
     • Source view  — `type=source`   → one row per order source (POS, Online
       Ordering, Kiosk, AI phone, Staff phone, …)
   Both calls carry the FE-resolved compStartDate/compEndDate, so every row
   arrives with its own `netSalesDelta` (% change in net sales).

   Per row the card shows the BE's figures as-is: netSales, grandTotal (total
   collected), orderCount, todayAvgOrder (= grandTotal ÷ orderCount) and
   netSalesDelta. The TOTAL row is computed here on the FE and is what the
   export's total row uses too. No React, no Redux.
   ═══════════════════════════════════════════════════════════════════════════ */

import type { LiveByChannelItem } from '../../../../interface/clickHouseInterface';
import type { SalesBreakdownView } from '../../../../redux/reportFilters/reportFiltersTypes';
import { computeDisplayPcts } from '../charts/reusable/DoughnutChart';
import { T } from '../../reportTokens';

export const VIEW_COPY: Record<SalesBreakdownView, { title: string; subtitle: string; column: string }> = {
    channel: { title: 'Sales by Channel', subtitle: 'Revenue share across order channels', column: 'Channel' },
    source: { title: 'Sales by Order Source', subtitle: 'Revenue share by where the order was placed', column: 'Source' },
};

/** A rendered row. */
export interface BreakdownRow {
    key: string;
    name: string;
    color: string;
    netSales: number;
    totalCollected: number;
    orders: number;
    /** BE `todayAvgOrder` (total collected ÷ orders); derived the same way if the BE omits it. */
    avgOrder: number;
    /** Share of net sales, 1 dp; the set always sums to 100.0. */
    sharePct: string;
    /** BE `netSalesDelta` — % change vs the comparison period; null = "N/A". */
    deltaPct: number | null;
}

export interface BreakdownTotals {
    netSales: number;
    totalCollected: number;
    orders: number;
    /** Total collected ÷ total orders — same basis as the rows' todayAvgOrder. */
    avgOrder: number;
}

const round2 = (n: number) => Math.round(n * 100) / 100;
const num = (v: unknown) => Number(v) || 0;
const numOrNull = (v: unknown): number | null => (v == null || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));
const norm = (s: string) => (s ?? '').toLowerCase().replace(/[\s\-_]+/g, '');

// Direct order routes that can come back in EITHER view (the BE returns e.g. "AI Phone" and
// "Staff Phone" as `channel` values) — same colour in both views. AI is tested before staff so
// "AI Phone" never falls into the generic phone rule.
const routeColor = (key: string): string | null => {
    if (/^ai|aiphone|voice/.test(key)) return T.blue;
    if (/staff|phone/.test(key)) return T.teal;
    if (/kiosk/.test(key)) return T.purple;
    return null;
};

// Channel view — the four POS channels keep the old doughnut's colours.
const channelColor = (key: string): string | null => {
    if (/instore|inhouse/.test(key)) return T.accent;
    if (/dinein/.test(key)) return '#E86B6B';
    if (/pickup/.test(key)) return T.orange;
    if (/delivery/.test(key)) return T.green;
    if (/online|website|web/.test(key)) return T.blueDark;
    return routeColor(key);
};

// Source view — story sources: POS, online / website, kiosk, AI phone, staff phone, in store.
const sourceColor = (key: string): string | null => {
    if (/^pos$|pointofsale/.test(key)) return T.accent;
    if (/online|website|web/.test(key)) return T.green;
    if (/instore|inhouse/.test(key)) return T.orange;
    return routeColor(key);
};

// Source-view rows get an info tooltip explaining what that source actually covers. AI is
// tested before the generic staff/phone rule for the same reason as routeColor above — "AI
// Phone" would otherwise match "phone" first. 3rd Party has no entry (no tooltip shown).
const SOURCE_INFO_TEXT: { pattern: RegExp; text: string }[] = [
    { pattern: /^ai|aiphone|voice/, text: 'Pick-up orders from AI voice agent' },
    { pattern: /staff|phone/, text: 'Pick-up and delivery orders taken by staff via phone' },
    { pattern: /^pos$|pointofsale/, text: 'Pick-up and dine-in orders from Bolt & Lounge in Brisque app & Contactless Dine-in (As applicable)' },
    { pattern: /online|website|web/, text: 'Pick-up & delivery orders from website & mobile app (As applicable)' },
    { pattern: /kiosk/, text: 'Dine-in & Pick-up orders from Front Kiosks & table kiosks (As applicable)' },
];

/** Info-icon tooltip text for a Source-view row name; null when the name has none (e.g. 3rd Party). */
export const getSourceInfoText = (name: string): string | null => {
    const key = norm(name);
    return SOURCE_INFO_TEXT.find((r) => r.pattern.test(key))?.text ?? null;
};

// Unknown DIRECT names still get a real colour — grey is reserved for 3rd party (SBS-06).
const FALLBACK_PALETTE = [T.blueDark, '#E86B6B', T.orange, T.teal];

const rowColor = (view: SalesBreakdownView, name: string, thirdParty: boolean, idx: number): string => {
    const key = norm(name);
    const named = view === 'source' ? sourceColor(key) : channelColor(key);
    if (named) return named;
    if (thirdParty) return T.t3;
    return FALLBACK_PALETTE[idx % FALLBACK_PALETTE.length];
};

/** `orderSource` is 'direct' for the outlet's own orders, otherwise the marketplace's name. */
const isThirdParty = (item: LiveByChannelItem) => {
    const src = String(item.orderSource ?? '').trim().toLowerCase();
    return src !== '' && src !== 'direct';
};

/** A row is shown only when it carries orders or money for the period. */
const hasActivity = (r: { orders: number; netSales: number; totalCollected: number }) =>
    r.orders > 0 || Math.abs(r.netSales) >= 0.005 || Math.abs(r.totalCollected) >= 0.005;

/**
 * API rows → card rows: rows sharing a name are merged (their delta can't be merged, so a merged
 * row reads "N/A"), empty rows dropped, sorted by net sales (highest first), 1-dp shares.
 */
export function buildRows(view: SalesBreakdownView, items: LiveByChannelItem[]): BreakdownRow[] {
    const map = new Map<string, Omit<BreakdownRow, 'color' | 'sharePct'> & { beAvg: number | null; count: number; thirdParty: boolean }>();
    items.forEach((item) => {
        const name = String(item.channel ?? '').trim() || 'Other';
        const key = name.toLowerCase();
        const prev = map.get(key);
        if (!prev) {
            map.set(key, {
                key, name,
                netSales: num(item.netSales),
                totalCollected: num(item.grandTotal),
                orders: num(item.orderCount),
                avgOrder: 0,
                beAvg: numOrNull(item.todayAvgOrder),
                deltaPct: numOrNull(item.netSalesDelta),
                count: 1,
                thirdParty: isThirdParty(item),
            });
        } else {
            prev.netSales += num(item.netSales);
            prev.totalCollected += num(item.grandTotal);
            prev.orders += num(item.orderCount);
            prev.beAvg = null;
            prev.deltaPct = null;
            prev.count += 1;
            // A merged row is 3rd party only if every part of it is.
            prev.thirdParty = prev.thirdParty && isThirdParty(item);
        }
    });

    const rows = Array.from(map.values())
        .map((r) => ({ ...r, netSales: round2(r.netSales), totalCollected: round2(r.totalCollected) }))
        .filter(hasActivity)
        .sort((a, b) => b.netSales - a.netSales || a.name.localeCompare(b.name));
    const shares = computeDisplayPcts(rows.map((r) => r.netSales), 1);

    return rows.map(({ beAvg, count, thirdParty, ...r }, i) => ({
        ...r,
        color: rowColor(view, r.name, thirdParty, i),
        avgOrder: beAvg ?? (r.orders > 0 ? r.totalCollected / r.orders : 0),
        sharePct: shares[i],
    }));
}

/** FE-computed total row — shown on the card and written as the export's total row. */
export function buildTotals(rows: BreakdownRow[]): BreakdownTotals {
    const netSales = round2(rows.reduce((s, r) => s + r.netSales, 0));
    const totalCollected = round2(rows.reduce((s, r) => s + r.totalCollected, 0));
    const orders = rows.reduce((s, r) => s + r.orders, 0);
    return { netSales, totalCollected, orders, avgOrder: orders > 0 ? totalCollected / orders : 0 };
}
